import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import Container from '@material-ui/core/Container'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import Typography from '@material-ui/core/Typography'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import { useTheme, makeStyles } from '@material-ui/core/styles'
import { useAuth } from 'lib/auth'
import { useFeatureFlags, flags } from 'lib/feature-flags'
import ScheduleCalendar from './calendar/schedule-calendar'
import AcademyScheduleView from './academy/academy-schedule-view'
import ScheduleSegmentControl from './schedule-segment-control'

const SCHEDULE_TAB_MY = 'my'
const SCHEDULE_TAB_ACADEMY = 'academy'

const SCHEDULE_SCOPE_OPTIONS = [
  { value: SCHEDULE_TAB_MY, label: 'My Schedule' },
  { value: SCHEDULE_TAB_ACADEMY, label: 'Academy' },
]

const useStyles = makeStyles(theme => ({
  containerStyles: {
    boxSizing: 'border-box',
    padding: '0 0 1rem',
    maxWidth: '100%',
    [theme.breakpoints.down('sm')]: {
      marginBottom: '60px',
    },
  },
  scopeToggle: {
    marginBottom: theme.spacing(2),
  },
  setupBanner: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
  },
  setupSection: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2.5),
    padding: theme.spacing(3),
    backgroundColor: '#E0EAFF',
    border: '1px solid #C5D4FF',
    borderRadius: 12,
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
  },
  setupCopy: {
    flex: 1,
    minWidth: 0,
  },
  setupTitle: {
    fontWeight: 700,
    fontSize: '1.125rem',
    lineHeight: 1.3,
    color: '#00205B',
    marginBottom: theme.spacing(0.75),
  },
  setupCaption: {
    fontSize: '0.875rem',
    lineHeight: 1.5,
    color: '#172024',
  },
  setupButton: {
    borderRadius: 20,
    textTransform: 'none',
    fontWeight: 700,
    fontSize: '0.9375rem',
    padding: theme.spacing(1.25, 3.5),
    whiteSpace: 'nowrap',
    flexShrink: 0,
    boxShadow: 'none',
    '&:hover': {
      boxShadow: '0 4px 12px rgba(0, 83, 255, 0.28)',
    },
    [theme.breakpoints.down('xs')]: {
      width: '100%',
    },
  },
}))

const BookingsTabs = ({ onboardingSchedule, onMetadataChange }) => {
  const classes = useStyles()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const { user } = useAuth()
  const [
    isAcademyScheduleUiEnabled,
    academyScheduleAcademyId,
  ] = useFeatureFlags([
    flags.FLAG_ACADEMY_SCHEDULE_UI,
    flags.FLAG_ACADEMY_SCHEDULE_UI_ACADEMY_ID,
  ])
  const showAcademyToggle = isAcademyScheduleUiEnabled && user?.isAcademyOwner
  const academyScheduleId =
    academyScheduleAcademyId || user?.ownedAcademyIds?.[0]
  const [scheduleTab, setScheduleTab] = useState(SCHEDULE_TAB_MY)
  const viewingAcademySchedule =
    showAcademyToggle && scheduleTab === SCHEDULE_TAB_ACADEMY

  const isOnboardingComplete =
    onboardingSchedule.isGroupEventCreated ||
    onboardingSchedule.isBookingTypeCreated

  useEffect(() => {
    if (!viewingAcademySchedule) return
    onMetadataChange?.({ rangeLabel: '' })
  }, [viewingAcademySchedule, onMetadataChange])

  const scheduleScopeToggle = showAcademyToggle ? (
    <Box className={classes.scopeToggle}>
      <ScheduleSegmentControl
        value={scheduleTab}
        onChange={(_, next) => next && setScheduleTab(next)}
        options={SCHEDULE_SCOPE_OPTIONS}
        ariaLabel="Schedule scope"
        isMobile={isMobile}
      />
    </Box>
  ) : null

  return (
    <Container className={classes.containerStyles}>
      {!isOnboardingComplete && (
        <Box className={classes.setupBanner}>
          <Box className={classes.setupSection}>
            <Box className={classes.setupCopy}>
              <Typography className={classes.setupTitle}>
                Need Help With Your Schedule Setup?
              </Typography>
              <Typography className={classes.setupCaption}>
                Revisit the PGA schedule page to track your progress and access
                additional information.
              </Typography>
            </Box>
            <Button
              variant="contained"
              color="primary"
              className={classes.setupButton}
              component={Link}
              to="/pga-coach/set-up-schedule"
            >
              Schedule Setup Guide
            </Button>
          </Box>
        </Box>
      )}
      {scheduleScopeToggle}
      {viewingAcademySchedule ? (
        <AcademyScheduleView academyId={academyScheduleId} />
      ) : (
        <ScheduleCalendar
          onMetadataChange={onMetadataChange}
          canBookStudent={isOnboardingComplete}
          bookingUrl={user?.coach?.bookingUrl}
        />
      )}
    </Container>
  )
}

export default BookingsTabs
